// src/components/MannequinPreview.jsx
import React, { Suspense, useEffect, useMemo, useRef } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { PerspectiveCamera } from "@react-three/drei";
import * as THREE from "three";

import Mannequin from "./Mannequin";

/**
 * Auto-fits the mannequin into the preview box by:
 * - waiting until the mannequin scene actually exists (async GLTF load)
 * - centering the model via its bounding box
 * - setting camera distance so the full height fits
 *
 * This runs INSIDE the R3F canvas so camera aspect is correct and it can retry.
 */
function AutoFit({ rigRef, view, depsKey }) {
  const { camera } = useThree();

  useEffect(() => {
    let tries = 0;
    let raf = 0;

    const fit = () => {
      const rig = rigRef.current;
      if (!rig) return;

      // Reset first so we don't accumulate offsets across runs
      rig.position.set(0, 0, 0);
      rig.rotation.set(0, 0, 0);
      rig.scale.set(1, 1, 1);
      rig.updateMatrixWorld(true);

      const box = new THREE.Box3().setFromObject(rig);
      const size = new THREE.Vector3();
      const center = new THREE.Vector3();
      box.getSize(size);
      box.getCenter(center);

      // Wait until mannequin actually exists in the scene (async GLTF)
      if (!isFinite(size.y) || size.y < 0.5) {
        tries += 1;
        if (tries < 45) raf = requestAnimationFrame(fit); // retry ~0.75s
        return;
      }

      // Center the model at origin
      rig.position.set(-center.x, -center.y, -center.z);

      // Lift slightly so feet aren't right on the bottom edge
      rig.position.y += size.y * 0.10 + 0.08;

      // Camera distance to fit full height
      const fov = (camera.fov * Math.PI) / 180;
      const dist = (size.y * 0.62) / Math.tan(fov / 2);

camera.position.set(0, 1.10, view === "back" ? -dist : dist);
camera.lookAt(0, 0.2, 0);
      camera.updateProjectionMatrix();
      camera.updateMatrixWorld();
    };

    raf = requestAnimationFrame(fit);
    return () => cancelAnimationFrame(raf);
  }, [camera, rigRef, view, depsKey]);

  return null;
}

export default function MannequinPreview({
  avatarParams,
  selectedItems = null,
  view = "front",
  garmentMetricsMap = null,
  bodyRefs = null,
}) {
  const rigRef = useRef(null);

  // keep mannequin remount behaviour consistent with LuxuryRoom
  const mannequinKey = useMemo(() => {
    const active =
      selectedItems?.dress ||
      selectedItems?.top ||
      selectedItems?.bottom ||
      selectedItems?.outer ||
      null;
    return `mnq-preview-${active?.id ?? active?.meshUrl ?? active?.name ?? "none"}`;
  }, [selectedItems]);

  // Any change here should trigger a refit (shape keys + skin + hair)
  const depsKey = useMemo(() => {
    const v = avatarParams || {};
    const skin = v?.skin?.base ?? "";
    const hairStyle = v?.hair?.style ?? "";
    const hairColor = v?.hair?.color ?? "";
    return [
      mannequinKey,
      String(v.Arm_up ?? 0),
      String(v.Bust_up ?? 0),
      String(v.Waist_up ?? 0),
      String(v.Hip_up ?? 0),
      String(v.Leg_up ?? 0),
      skin,
      hairStyle,
      hairColor,
    ].join("|");
  }, [
    mannequinKey,
    avatarParams?.Arm_up,
    avatarParams?.Bust_up,
    avatarParams?.Waist_up,
    avatarParams?.Hip_up,
    avatarParams?.Leg_up,
    avatarParams?.skin?.base,
    avatarParams?.hair?.style,
    avatarParams?.hair?.color,
  ]);

  return (
    <div className="mannequin-preview">
      <Canvas
        shadows
        gl={{
          antialias: true,
          outputColorSpace: THREE.SRGBColorSpace,
          alpha: true,
        }}
        onCreated={({ gl }) => {
          gl.toneMapping = THREE.ACESFilmicToneMapping;
          gl.toneMappingExposure = 1.05;
        }}
      >
        <PerspectiveCamera
          makeDefault
          fov={30}
          position={view === "back" ? [0, 1.55, -3.0] : [0, 1.55, 3.0]}
          near={0.1}
          far={60}
        />

        {/* clean studio lighting */}
        <ambientLight intensity={0.55} color="#fff6ec" />
        <directionalLight
          position={[3, 5, 2]}
          intensity={1.2}
          color="#ffffff"
          castShadow
          shadow-mapSize-width={2048}
          shadow-mapSize-height={2048}
          shadow-bias={-0.00015}
        />
        <directionalLight position={[-2, 3, -2]} intensity={0.65} color="#ffffff" />

        {/* soft ground shadow (invisible plane) */}
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]} receiveShadow>
          <planeGeometry args={[6, 6]} />
          <shadowMaterial opacity={0.18} />
        </mesh>

        <Suspense fallback={null}>
          {/* Rig wrapper (AutoFit moves this) */}
          <group ref={rigRef}>
            <Mannequin
  key={mannequinKey}
  avatarParams={avatarParams}
  selectedItems={selectedItems}
  poseMode={view}
  garmentMetricsMap={garmentMetricsMap}
  bodyRefs={bodyRefs}
/>
          </group>

          <AutoFit rigRef={rigRef} view={view} depsKey={depsKey} />
        </Suspense>
      </Canvas>
    </div>
  );
}